import { Injectable, inject } from '@angular/core';
import { LoadedPolicy, loadPolicy } from '@open-policy-agent/opa-wasm';
import { UserDataFacade } from '@ra-state';
import { catchError, concatMap, map, Observable, shareReplay } from 'rxjs';
import { DataService } from './data.service';
import { LoggerService } from './logger.service';

export interface PolicyData<T> {
  input: T;
}

export type Operator = 'all' | 'any';

const PermissionEvaluators = {
  canApproveAccess: (permsEvalResult: PermsEvaluationResult): boolean =>
    permsEvalResult.permissions.canApproveFor.length > 0,
  canInvite: (permsEvalResult: PermsEvaluationResult): boolean => permsEvalResult.permissions.canInviteTo.length > 0,
  canManageAccess: (permsEvalResult: PermsEvaluationResult): boolean =>
    permsEvalResult.has_tenant_privilege || permsEvalResult.privileged_services.length > 0,
  canAllocateEntitlements: (permsEvalResult: PermsEvaluationResult): boolean => permsEvalResult.has_tenant_privilege,
  canEditTenantSettings: (permsEvalResult: PermsEvaluationResult): boolean => permsEvalResult.has_tenant_privilege,
};
export type Permission = keyof typeof PermissionEvaluators;

export type PermissionCondition = {
  operator?: Operator;
  permissions: Permission[];
};

export interface PermsEvaluationResult {
  has_tenant_privilege: boolean;
  permissions: {
    canApproveFor: string[];
    canInviteTo: string[];
  };
  privileged_services: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsService {
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly dataService = inject(DataService);

  public evaluate$<TInput, TResult>(policyData: PolicyData<TInput>): Observable<TResult> {
    const entrypoint = 'permissions';
    return this.loadOpaPolicy$().pipe(
      map((policyWasm) => {
        return {
          response: policyWasm.evaluate(policyData.input, entrypoint),
        };
      }),
      map((payload) => {
        // Parse result as recommended by
        // https://github.com/open-policy-agent/npm-opa-wasm#evaluate-the-policy
        const parsed = payload.response?.length > 0 ? payload.response[0].result : null;

        if (parsed === null) {
          this.logger.warn(
            'OPA evaluate did not generate a response. Bug?' +
              `\n\tEntrypoint ${entrypoint}\n\tInput:${JSON.stringify(policyData, null, 2)}`,
          );
        } else {
          this.logger.log(
            `OPA evaluate: Entrypoint=${entrypoint}\n` +
              `\twhy: Evaluating permissions with OPA\n` +
              `\tresult: ${parsed}\n` +
              `\tOPA input & result:\n`,
            policyData,
            payload.response,
          );
        }
        return parsed;
      }),
    );
  }

  checkImpl$(requestedPerms: Permission[], op: Operator = 'any'): Observable<boolean> {
    return this.userDataFacade.userPermissionsEvalResult$.pipe(
      map((userPermissions) => {
        const perms = requestedPerms ?? [];
        const evaluations = perms.map((perm) => PermissionEvaluators[perm](userPermissions));
        const identity = op === 'all';
        const result = evaluations.reduce((acc, curr) => (op === 'all' ? acc && curr : acc || curr), identity);
        this.logger.debug(op, result, perms);
        return result;
      }),
    );
  }

  hasAll$ = (requestedPerms: Permission[]): Observable<boolean> => this.checkImpl$(requestedPerms, 'all');
  hasAny$ = (requestedPerms: Permission[]): Observable<boolean> => this.checkImpl$(requestedPerms, 'any');

  // Load the WebAssemply
  loadOpaPolicy$(): Observable<LoadedPolicy> {
    return this.dataService.getOpaPolicy$().pipe(
      concatMap((buffer) => {
        if (buffer) {
          return loadPolicy(buffer);
        } else {
          throw new Error('OPA policy is not available');
        }
      }),
      catchError((err: unknown) => {
        this.logger.error('Failed to load policy', err);
        throw new Error('Failed to load policy. Check browser console log');
      }),
      // One instance is all we need
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  logger: LoggerService;

  constructor() {
    const logger = inject(LoggerService);

    this.logger = logger.withContext('OpaService');
  }
}
